import styled, {keyframes, css} from 'styled-components';
import {media} from '../../styles/components/media-queries';
import PromoBanner from '../../components/PromoBanner/PromoBanner';

const stepPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.45);
  }
  70% {
    box-shadow: 0 0 0 0.8rem rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

const indeterminateSlide = keyframes`
  0% {
    left: -45%;
  }
  100% {
    left: 100%;
  }
`;

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100rem;
  margin: 0 auto 1rem;
  padding: 0 2rem;
  ${media.smaller`
    margin: 1rem auto;
    padding: 0 1rem;
  `};
`;

export const PageTitle = styled.h1`
  color: ${p => p.theme.secondaryColor};
  font-size: 3.6rem;
  font-weight: lighter;
  margin-bottom: 2rem;
  ${media.smaller`
    font-size: 2.4rem;
    margin-bottom: 1rem;
  `};
`;

export const ComparisonHeader = styled.div`
  margin-bottom: 1.5rem;
`;

export const ComparisonTitle = styled.h1`
  color: ${p => p.theme.secondaryColor};
  font-size: 2.6rem;
  font-weight: bold;
  margin: 0 0 0.4rem;
  ${media.smaller`
    font-size: 2.1rem;
  `};
`;

export const ComparisonSubtitle = styled.p`
  font-size: 1.4rem;
  line-height: 2rem;
  color: ${p => p.theme.tertiaryTextColor};
  margin: 0;
`;

export const SectionTitle = styled.h2`
  color: ${p => p.theme.secondaryColor};
  font-size: 2.4rem;
  font-weight: lighter;
  margin: 2rem 0 1rem;
  ${media.smaller`
    font-size: 1.8rem;
  `};
`;

export const PageIntro = styled.div`
  font-size: 1.6rem;
  line-height: 2.4rem;
  color: ${p => p.theme.textColor};
  margin-bottom: 2rem;

  p {
    margin: 0 0 1rem;
  }

  strong {
    color: ${p => p.theme.secondaryColor};
  }
  ${media.smaller`
    margin: 0 1rem 1rem;
  `};
`;

export const IntroHighlight = styled.div`
  text-align: center;
  color: ${p => p.theme.blackColor};
  line-height: 3rem;
  font-size: 2.2rem;
  position: absolute;
  bottom: 2rem;
  width: 100%;
  p {
    margin: 0 0 0.4rem;
  }
  b {
    font-size: 2.6rem;
  }
  ${media.smaller`
    position: relative;
    padding-top: 2rem;
    font-size: 1.8rem;
    b {
      font-size: 2rem;
    }
  `};
`;

export const IntroHero = styled.div`
  padding-bottom: 2rem;
  position: relative;
  ${media.smaller`
    padding-bottom: 1;
  `};
`;

export const CompactPromoBanner = styled(PromoBanner)`
  max-width: 70rem;
  margin: 0 auto;
`;

export const UploadSection = styled.div`
  margin-bottom: 3rem;
  padding: 2rem;
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.4rem;
  background-color: ${p => p.theme.backgroundColor || '#fff'};
  position: relative;
`;

export const ExpandedVideo = styled.div`
  border-radius: 0.7rem;
  overflow: hidden;
  margin-bottom: 2rem;
`;

export const VideoFrame = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  background: #0d1210;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`;

export const FulfillmentChoice = styled.div`
  margin: 0 0 2rem;
`;

export const FulfillmentChoiceLabel = styled.span`
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  color: ${p => p.theme.textColor};
  margin-bottom: 0.4rem;
`;

export const FulfillmentChoiceHint = styled.p`
  font-size: 1.3rem;
  color: ${p => p.theme.tertiaryTextColor};
  margin: 0 0 1.2rem;
`;

export const FulfillmentOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  ${media.smaller`
    grid-template-columns: 1fr;
    gap: 0.8rem;
  `};
`;

export const FulfillmentChoiceOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 1.2rem 1.5rem;
  border: 0.1rem solid
    ${p => (p.$selected ? p.theme.secondaryColor : p.theme.borderColor)};
  border-radius: 0.6rem;
  background-color: ${p =>
    p.$selected ? 'rgba(0, 128, 0, 0.06)' : 'transparent'};
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: ${p => p.theme.secondaryColor};
  }

  input[type='radio'] {
    cursor: pointer;
    margin-top: 0.3rem;
    accent-color: ${p => p.theme.secondaryColor};
  }

  strong {
    display: block;
    font-size: 1.5rem;
    color: ${p => p.theme.textColor};
  }

  span {
    display: block;
    font-size: 1.3rem;
    color: ${p => p.theme.tertiaryTextColor};
    margin-top: 0.2rem;
  }
`;

export const DropZone = styled.div`
  border: 0.2rem dashed
    ${p => (p.$isDragOver ? p.theme.secondaryColor : p.theme.borderColor)};
  border-radius: 0.4rem;
  padding: 3rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  background-color: ${p =>
    p.$isDragOver ? 'rgba(0, 128, 0, 0.05)' : 'transparent'};

  &:hover {
    border-color: ${p => p.theme.secondaryColor};
  }

  p {
    font-size: 1.6rem;
    color: ${p => p.theme.textColor};
    margin: 0.5rem 0;
  }
`;

export const DropZoneIcon = styled.svg`
  width: 4rem;
  height: 4rem;
  margin-bottom: 1rem;
  fill: none;
  stroke: ${p => p.theme.secondaryColor};
  stroke-width: 1.5;
`;

export const DropZoneLabel = styled.p`
  font-size: 1.8rem !important;
  font-weight: bold;
  color: ${p => p.theme.secondaryColor} !important;
`;

// Standalone box above the drop zone listing what makes a good upload, so the
// checklist stays readable before the customer commits to picking a file.
export const ChecklistBox = styled.div`
  margin: 0 0 2rem;
  padding: 1.5rem 2rem;
  border-radius: 0.4rem;
  background-color: ${p => p.theme.lightBackgroundColor || '#f5f5f5'};
`;

export const ChecklistList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    position: relative;
    font-size: 1.4rem;
    color: ${p => p.theme.textColor};
    padding-left: 2rem;
    margin: 0.8rem 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }

    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: ${p => p.theme.secondaryColor};
      font-weight: bold;
    }
  }
`;

export const DropZoneFormats = styled.p`
  font-size: 1.3rem !important;
  color: ${p => p.theme.tertiaryTextColor} !important;
  margin-top: 1rem !important;
`;

export const TermsRow = styled.div`
  margin-top: 1.5rem;
  padding: 1.2rem 1.5rem;
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.6rem;
  background-color: ${p => p.theme.lightBackgroundColor || '#fafafa'};

  label {
    display: flex;
    align-items: flex-start;
    gap: 0.8rem;
    font-size: 1.4rem;
    color: ${p => p.theme.textColor};
    cursor: pointer;
  }

  input[type='checkbox'] {
    cursor: pointer;
    margin-top: 0.3rem;
    accent-color: ${p => p.theme.secondaryColor};
  }

  a {
    color: ${p => p.theme.secondaryColor};
    text-decoration: underline;
  }
`;

export const TermsExplanation = styled.p`
  font-size: 1.3rem;
  color: ${p => p.theme.tertiaryTextColor};
  margin: 0.6rem 0 0 2.4rem;
`;

export const FileList = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const FileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: ${p => p.theme.lightBackgroundColor || '#f5f5f5'};
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: ${p => p.theme.textColor};

  button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.6rem;
    color: ${p => p.theme.tertiaryColor};
    padding: 0;
    line-height: 1;
  }
`;

export const UploadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 1.5rem;
  padding: 1.2rem 3rem;
  font-size: 1.6rem;
  font-weight: bold;
  color: ${p => p.theme.primaryTextColor};
  background-color: ${p => p.theme.secondaryColor};
  border: none;
  border-radius: 0.6rem;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const UploadButtonCaption = styled.p`
  text-align: center;
  font-size: 1.3rem;
  color: ${p => p.theme.tertiaryTextColor};
  margin: 1rem 0 0;
`;

// Matches CreateSignupPage.style.js's FieldError — same red used for its
// Input's $hasError border and this page's own ErrorMessage.
export const FieldError = styled.p`
  font-size: 1.3rem;
  color: #d32f2f;
  margin: 0.8rem 0 0;
`;

export const UploadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  z-index: 1;
  border-radius: 0.4rem;

  p {
    font-size: 1.6rem;
    color: ${p => p.theme.secondaryColor};
    font-weight: bold;
  }

  span {
    font-size: 1.4rem;
    color: ${p => p.theme.tertiaryTextColor};
  }
`;

export const SuccessMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background-color: rgba(0, 128, 0, 0.08);
  border: 0.1rem solid #2e7d32;
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: #2e7d32;
`;

export const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background-color: rgba(211, 47, 47, 0.08);
  border: 0.1rem solid #d32f2f;
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: #d32f2f;
`;

export const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.4rem;

  th {
    text-align: left;
    padding: 1.2rem 1rem;
    border-bottom: 0.2rem solid ${p => p.theme.borderColor};
    color: ${p => p.theme.secondaryColor};
    font-weight: bold;
    white-space: nowrap;
  }

  td {
    padding: 1.2rem 1rem;
    border-bottom: 0.1rem solid ${p => p.theme.borderColor};
    color: ${p => p.theme.textColor};
  }

  tbody tr {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: ${p => p.theme.lightBackgroundColor || '#f9f9f9'};
    }
  }

  /* Small screens keep Supplier, Invoice No. and View — the date and savings
     columns (3-5) are dropped first, then Invoice No. on the smallest. */
  ${media.small`
    th:nth-child(3),
    td:nth-child(3),
    th:nth-child(4),
    td:nth-child(4),
    th:nth-child(5),
    td:nth-child(5) {
      display: none;
    }
  `};

  ${media.smaller`
    th:nth-child(2),
    td:nth-child(2) {
      display: none;
    }
  `};
`;

export const StatusBadge = styled.span`
  && {
    display: inline-block;
    padding: 0.3rem 0.8rem;
    border-radius: 0.3rem;
    font-size: 1.2rem;
    font-weight: bold;
    white-space: nowrap;
    color: ${p => {
      switch (p.$status) {
        case 'READY_FOR_REVIEW':
        case 'SUBMITTED':
          return '#6d5600';
        default:
          return '#fff';
      }
    }};
    background-color: ${p => {
      switch (p.$status) {
        case 'PROCESSING':
          return '#1976d2';
        case 'READY_FOR_REVIEW':
        case 'SUBMITTED':
          return '#fff3cd';
        case 'MATCHED':
        case 'EMAIL_SENT':
          return '#2e7d32';
        case 'FAILED':
          return '#d32f2f';
        default:
          return '#757575';
      }
    }};
  }
`;

export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
  font-size: 1.4rem;

  button {
    padding: 0.8rem 1.6rem;
    font-size: 1.4rem;
    cursor: pointer;
    background-color: ${p => p.theme.secondaryColor};
    color: ${p => p.theme.primaryTextColor};
    border: none;
    border-radius: 0.4rem;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  span {
    color: ${p => p.theme.textColor};
  }
`;

export const ViewLink = styled.span`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${p => p.theme.secondaryColor};
  white-space: nowrap;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  font-size: 1.6rem;
  color: ${p => p.theme.tertiaryTextColor};
`;

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 4rem 0;
`;

// Remaining free-comparison allowance shown above the anonymous upload form.
export const QuotaBanner = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;
  background-color: ${p => p.theme.lightBackgroundColor || '#f5f5f5'};
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: ${p => p.theme.textColor};
`;

// Shown instead of the upload form once an anonymous visitor's quota is
// blocked, or when the feature is switched off server-side.
export const RegisterPrompt = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  background-color: ${p => p.theme.lightBackgroundColor || '#f5f5f5'};
  border-radius: 0.4rem;

  p {
    font-size: 1.6rem;
    color: ${p => p.theme.textColor};
    margin: 0 0 2rem;
  }
`;

export const RegisterPromptLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;

  a {
    font-size: 1.6rem;
    font-weight: bold;
    color: ${p => p.theme.primaryTextColor};
    background-color: ${p => p.theme.secondaryColor};
    padding: 1.1rem 2.4rem;
    border-radius: 0.4rem;
    text-decoration: none;

    &:last-child {
      background-color: transparent;
      color: ${p => p.theme.secondaryColor};
      border: 0.1rem solid ${p => p.theme.secondaryColor};
    }
  }
`;

// Shown instead of the upload form once an anonymous visitor has used up
// their free daily comparisons.
export const PromptCard = styled.div`
  padding: 2.4rem 3rem;
  border: 0.1rem solid ${p => p.theme.secondaryColor};
  border-radius: 0.8rem;
  margin-bottom: 1.5rem;
  ${media.smaller`
    padding: 2rem;
  `};
`;

export const PromptCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;

  ${media.smaller`
    flex-direction: column;
    align-items: stretch;
  `};
`;

export const PromptCardTitle = styled.h3`
  font-size: 2rem;
  font-weight: bold;
  color: ${p => p.theme.textColor};
  margin: 0 0 0.8rem;
`;

export const PromptCardText = styled.p`
  font-size: 1.5rem;
  color: ${p => p.theme.textColor};
  margin: 0;
`;

export const PromptCardButton = styled.a`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  font-weight: bold;
  color: ${p => p.theme.primaryTextColor};
  background-color: ${p => p.theme.secondaryColor};
  padding: 1.4rem 2.8rem;
  border-radius: 0.6rem;
  text-decoration: none;
  white-space: nowrap;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

export const PromptCardDivider = styled.hr`
  border: none;
  border-top: 0.1rem solid ${p => p.theme.borderColor};
  margin: 2rem 0;
`;

export const PromptCardFooter = styled.p`
  font-size: 1.5rem;
  color: ${p => p.theme.textColor};
  margin: 0;

  a {
    color: ${p => p.theme.secondaryColor};
    font-weight: bold;
    text-decoration: underline;
  }
`;

export const DetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100rem;
  margin: 4rem auto 2rem;
  padding: 0 2rem;
  ${media.smaller`
    margin: 1rem auto;
    padding: 0 1rem;
  `};
`;

export const BackLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.4rem;
  color: ${p => p.theme.secondaryColor};
  padding: 0;
  margin-bottom: 1rem;
  text-decoration: underline;
  align-self: flex-start;

  &:hover {
    opacity: 0.8;
  }
`;

export const OverviewCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem 2rem;
  padding: 1.2rem 1.6rem;
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.4rem;
  margin-bottom: 2rem;
  background-color: ${p => p.theme.backgroundColor || '#fff'};
`;

export const OverviewItem = styled.div`
  min-width: 12rem;

  label {
    display: block;
    font-size: 1.1rem;
    color: ${p => p.theme.tertiaryTextColor};
    text-transform: uppercase;
    margin-bottom: 0.2rem;
  }

  > span {
    display: inline-block;
    margin-top: 0.5rem;
    font-size: 1.4rem;
    color: ${p => p.theme.textColor};
    font-weight: ${p => (p.$bold ? 'bold' : 'normal')};
  }
`;

export const AddAllButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 1rem;
  padding: 1.4rem 2.4rem;
  font-size: 1.6rem;
  font-weight: bold;
  color: ${p => p.theme.primaryTextColor};
  background-color: ${p => p.theme.secondaryColor};
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${p => p.theme.secondaryColorHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Puts two AddAllButtons side by side (favourites / shopping list) instead of
// each stacking full-width — flex-basis 0 from `flex: 1` on the children
// overrides AddAllButton's own `width: 100%`, splitting the row evenly.
export const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  & > button {
    flex: 1;
    margin-bottom: 0;
  }

  ${media.smaller`
    flex-direction: column;
  `};
`;

// Secondary, lower-commitment action under the primary "add all" button: add
// only the lines where JJ actually comes out cheaper.
export const SecondaryAddLink = styled.button`
  display: block;
  width: 100%;
  margin: 0 auto 2rem;
  padding: 0.4rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  color: ${p => p.theme.secondaryColor};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${p => p.theme.secondaryColorHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Hero card: the headline figure of the whole page — how much the customer
// would save buying from JJ, counting only the lines where JJ is cheaper.
export const SavingsHero = styled.div`
  text-align: center;
  padding: 2.4rem 2rem;
  border: 0.1rem solid #cfe9d8;
  border-radius: 0.6rem;
  margin-bottom: 1.5rem;
  background-color: #eef7f1;
`;

export const SavingsHeroLabel = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  letter-spacing: 0.1rem;
  text-transform: uppercase;
  color: #2e7d32;
  margin-bottom: 0.6rem;
`;

export const SavingsHeroValue = styled.div`
  font-size: 4.8rem;
  font-weight: bold;
  line-height: 1.1;
  color: #1b5e20;
  ${media.smaller`
    font-size: 3.6rem;
  `};
`;

export const SavingsHeroCaption = styled.div`
  font-size: 1.5rem;
  color: ${p => p.theme.textColor};
  margin-top: 0.6rem;
`;

export const LineItemCard = styled.div`
  /* Product swaps get a distinct purple border + tint so the customer can see
     at a glance that we've suggested a JJ alternative to the brand match. */
  border: ${p => (p.$productSwap ? '0.15rem' : '0.1rem')} solid
    ${p =>
      p.$productSwap
        ? '#7c3aed'
        : p.$noMatch
        ? 'rgba(224, 134, 0, 0.35)'
        : p.theme.borderColor};
  border-radius: 0.4rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
  background-color: ${p =>
    p.$productSwap
      ? 'rgba(124, 58, 237, 0.04)'
      : p.$noMatch
      ? 'rgba(224, 134, 0, 0.04)'
      : p.theme.backgroundColor || '#fff'};
`;

export const ProductSwapNote = styled.div`
  margin-top: 0.8rem;
  padding: 0.8rem 1rem;
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: ${p => p.theme.tertiaryTextColor};
  background-color: rgba(124, 58, 237, 0.06);
  border-left: 0.3rem solid #7c3aed;
`;

export const ProductSwapLabel = styled.span`
  display: inline-block;
  font-size: 1.1rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.05rem;
  color: #7c3aed;
  margin-bottom: 0.3rem;
`;

export const LineItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
  ${media.smaller`
    flex-direction: column;
  `};
`;

export const LineItemDescription = styled.div`
  font-size: 1.6rem;
  font-weight: bold;
  color: ${p => p.theme.textColor};
`;

export const LineItemDetails = styled.div`
  font-size: 1.4rem;
  color: ${p => p.theme.tertiaryTextColor};
  margin-top: 0.3rem;
`;

export const LineItemMatch = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-radius: 0.4rem;
  background-color: ${p => p.theme.lightBackgroundColor || '#f9f9f9'};
  gap: 1rem;
  ${media.smaller`
    flex-direction: column;
    align-items: flex-start;
  `};
`;

export const MatchInfo = styled.div`
  flex: 1 1 auto;
  font-size: 1.4rem;
  color: ${p => p.theme.textColor};

  strong {
    display: block;
    margin-bottom: 0.2rem;
  }
`;

export const ProductLink = styled.a`
  color: ${p => p.theme.secondaryColor};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const SavingBadge = styled.span`
  font-size: 1.4rem;
  font-weight: bold;
  padding: 0.4rem 0.8rem;
  border-radius: 0.3rem;
  white-space: nowrap;
  color: #fff;
  background-color: ${p => (p.$positive ? '#2e7d32' : '#d32f2f')};
`;

export const NoMatchLabel = styled.span`
  font-size: 1.4rem;
  color: ${p => p.theme.tertiaryTextColor};
  font-style: italic;
`;

export const InferredNote = styled.div`
  font-size: 1.3rem;
  color: ${p => p.theme.tertiaryTextColor};
  font-style: italic;
  margin-top: 0.5rem;
`;

export const ProcessingMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  font-size: 1.6rem;
  color: ${p => p.theme.tertiaryTextColor};

  h3 {
    color: ${p => p.theme.secondaryColor};
    margin-bottom: 1rem;
  }
`;

// Staged progress tracker shown on the results page while the invoice moves
// through reading -> matching -> savings. Visible forward motion makes the wait
// feel shorter than a single indeterminate spinner.
export const ProgressSteps = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 2rem;
  margin-bottom: 1rem;
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.4rem;
  background-color: ${p => p.theme.backgroundColor || '#fff'};
  ${media.smaller`
    flex-wrap: wrap;
    gap: 0.8rem 0.4rem;
  `};
`;

export const ProgressStep = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.4rem;
  font-weight: bold;
  color: ${p => {
    if (p.$state === 'pending') return p.theme.tertiaryTextColor;
    if (p.$state === 'active') return '#1976d2';
    return '#2e7d32';
  }};
`;

export const ProgressDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  font-size: 1.3rem;
  color: #fff;
  background-color: ${p => {
    if (p.$state === 'pending') return '#bdbdbd';
    if (p.$state === 'active') return '#1976d2';
    return '#2e7d32';
  }};
  ${p =>
    p.$state === 'active' &&
    css`
      animation: ${stepPulse} 1.6s ease-out infinite;
    `};
`;

export const ProgressConnector = styled.span`
  flex: 1 1 2rem;
  min-width: 1.5rem;
  height: 0.2rem;
  background-color: ${p => (p.$filled ? '#2e7d32' : p.theme.borderColor)};
  ${media.smaller`
    flex-basis: 1rem;
  `};
`;

export const ProgressCaption = styled.p`
  font-size: 1.4rem;
  color: ${p => p.theme.tertiaryTextColor};
  margin: 0 0 1rem;
`;

// Indeterminate bar — a stripe sliding across a track — to show the long
// "reading" stage is actively working without claiming a specific percentage.
export const ProgressBar = styled.div`
  position: relative;
  height: 0.4rem;
  margin: 0 0 2rem;
  border-radius: 0.2rem;
  background-color: ${p => p.theme.borderColor};
  overflow: hidden;
`;

export const ProgressBarFill = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 45%;
  border-radius: 0.2rem;
  background-color: #1976d2;
  animation: ${indeterminateSlide} 1.3s ease-in-out infinite;
`;

// Per-line placeholder while a line's match is still being computed.
export const MatchingPlaceholder = styled.div`
  padding: 1rem;
  border-radius: 0.4rem;
  background-color: ${p => p.theme.lightBackgroundColor || '#f9f9f9'};
  font-size: 1.4rem;
  font-style: italic;
  color: ${p => p.theme.tertiaryTextColor};
`;
